import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { Puja, CreatePuja, PujaLine, PujaBidhiLink, PujaSankalpaLink, PujaGranthaLink } from '../types';
import { pujaApi } from '../services/api';

interface PujaState {
  pujas: Puja[];
  currentPuja: Puja | null;
  loading: boolean;
  currentPujaLoading: boolean;
  error: string | null;
}

const initialState: PujaState = {
  pujas: [],
  currentPuja: null,
  loading: false,
  currentPujaLoading: false,
  error: null,
};

export const fetchPujas = createAsyncThunk('puja/fetchAll', async () => {
  const response = await pujaApi.getAll();
  return response.data;
});

export const fetchPujaById = createAsyncThunk('puja/fetchById', async (id: number) => {
  const response = await pujaApi.getById(id);
  return response.data;
});

export const createPuja = createAsyncThunk('puja/create', async (puja: CreatePuja) => {
  const response = await pujaApi.create(puja);
  return response.data;
});

export const updatePuja = createAsyncThunk('puja/update', async ({ id, data }: { id: number; data: { name: string; published?: boolean; metadata?: { bidha: string; upabidha?: string; prakar?: string | null; upaprakar?: string | null; aupaprakar?: string | null; [key: string]: any } } }) => {
  const response = await pujaApi.update(id, data);
  return response.data;
});

export const deletePuja = createAsyncThunk('puja/delete', async (id: number) => {
  await pujaApi.delete(id);
  return id;
});

export const updatePujaLine = createAsyncThunk('puja/updateLine', async ({ lineId, data }: { lineId: number; data: { content: string; order?: number; metadata?: Record<string, string>; isVisible?: boolean } }) => {
  const response = await pujaApi.updateLine(lineId, data);
  return { lineId, data };
});

export const deletePujaLine = createAsyncThunk('puja/deleteLine', async (lineId: number) => {
  await pujaApi.deleteLine(lineId);
  return lineId;
});

export const addPujaLines = createAsyncThunk('puja/addLines', async ({ pujaId, lines }: { pujaId: number; lines: { content: string; order: number; pujaId: number }[] }) => {
  const response = await pujaApi.addLines(pujaId, lines);
  return { pujaId, lines: response.data };
});

export const bulkUpdatePujaLineOrder = createAsyncThunk(
  'puja/bulkUpdateLineOrder',
  async (updates: { pujaLineId: number; changedOrder: number }[]) => {
    await pujaApi.bulkUpdateLineOrder(updates);
    return updates;
  }
);

const pujaSlice = createSlice({
  name: 'puja',
  initialState,
  reducers: {
    addPujaBidhiLink: (state, action: PayloadAction<{ pujaLineId: number; link: PujaBidhiLink }>) => {
      const { pujaLineId, link } = action.payload;
      const addLink = (puja: Puja) => {
        puja.lines?.forEach(line => {
          if (line.id === pujaLineId) {
            if (!line.bidhiLinks) {
              line.bidhiLinks = [];
            }
            // Avoid duplicates by id
            if (!line.bidhiLinks.find(l => l.id === link.id)) {
              line.bidhiLinks.push(link);
            }
          }
        });
      };
      state.pujas.forEach(addLink);
      if (state.currentPuja) {
        addLink(state.currentPuja);
      }
    },
    addPujaSankalpaLink: (state, action: PayloadAction<{ pujaLineId: number; link: PujaSankalpaLink }>) => {
      const { pujaLineId, link } = action.payload;
      const addLink = (puja: Puja) => {
        puja.lines?.forEach(line => {
          if (line.id === pujaLineId) {
            if (!line.sankalpaLinks) {
              line.sankalpaLinks = [];
            }
            if (!line.sankalpaLinks.find(l => l.id === link.id)) {
              line.sankalpaLinks.push(link);
            }
          }
        });
      };
      state.pujas.forEach(addLink);
      if (state.currentPuja) {
        addLink(state.currentPuja);
      }
    },
    removePujaBidhiLink: (state, action: PayloadAction<{ linkId: number }>) => {
      const { linkId } = action.payload;
      const removeLink = (puja: Puja) => {
        puja.lines?.forEach(line => {
          if (line.bidhiLinks) {
            line.bidhiLinks = line.bidhiLinks.filter(link => link.id !== linkId);
          }
        });
      };
      state.pujas.forEach(removeLink);
      if (state.currentPuja) {
        removeLink(state.currentPuja);
      }
    },
    removePujaSankalpaLink: (state, action: PayloadAction<{ linkId: number }>) => {
      const { linkId } = action.payload;
      const removeLink = (puja: Puja) => {
        puja.lines?.forEach(line => {
          if (line.sankalpaLinks) {
            line.sankalpaLinks = line.sankalpaLinks.filter(link => link.id !== linkId);
          }
        });
      };
      state.pujas.forEach(removeLink);
      if (state.currentPuja) {
        removeLink(state.currentPuja);
      }
    },
    addPujaGranthaLink: (state, action: PayloadAction<{ pujaLineId: number; link: PujaGranthaLink }>) => {
      const { pujaLineId, link } = action.payload;
      const addLink = (puja: Puja) => {
        puja.lines?.forEach(line => {
          if (line.id === pujaLineId) {
            if (!line.granthaLinks) {
              line.granthaLinks = [];
            }
            if (!line.granthaLinks.find(l => l.id === link.id)) {
              line.granthaLinks.push(link);
            }
          }
        });
      };
      state.pujas.forEach(addLink);
      if (state.currentPuja) {
        addLink(state.currentPuja);
      }
    },
    removePujaGranthaLink: (state, action: PayloadAction<{ linkId: number }>) => {
      const { linkId } = action.payload;
      const removeLink = (puja: Puja) => {
        puja.lines?.forEach(line => {
          if (line.granthaLinks) {
            line.granthaLinks = line.granthaLinks.filter(link => link.id !== linkId);
          }
        });
      };
      state.pujas.forEach(removeLink);
      if (state.currentPuja) {
        removeLink(state.currentPuja);
      }
    },
    updatePujaBidhiLinkOrder: (state, action: PayloadAction<{ pujaLineId: number; position: number; updates: { pujaBidhiLinkId: number; changedOrder: number }[] }>) => {
      const { pujaLineId, position, updates } = action.payload;
      
      const updateLineLinks = (line: PujaLine) => {
        if (line.id === pujaLineId && line.bidhiLinks) {
          // Create a map of linkId to new order
          const orderMap = new Map<number, number>();
          updates.forEach(update => {
            orderMap.set(update.pujaBidhiLinkId, update.changedOrder);
          });
          
          // Update order for links at this position
          line.bidhiLinks.forEach(link => {
            if (link.position === position && orderMap.has(link.id)) {
              link.order = orderMap.get(link.id)!;
            }
          });
          
          // Sort by order after updating
          line.bidhiLinks.sort((a, b) => (a.order || 0) - (b.order || 0));
        }
      };

      // Update in pujas array
      state.pujas.forEach(puja => {
        puja.lines?.forEach(updateLineLinks);
      });

      // Also update currentPuja if it matches
      if (state.currentPuja) {
        state.currentPuja.lines?.forEach(updateLineLinks);
      }
    },
    addPujaToState: (state, action: PayloadAction<Puja>) => {
      const puja = action.payload;
      // Check if puja already exists (by id)
      const existingIndex = state.pujas.findIndex(p => p.id === puja.id);
      if (existingIndex !== -1) {
        state.pujas[existingIndex] = puja;
      } else {
        state.pujas.push(puja);
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchPujas.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPujas.fulfilled, (state, action) => {
        state.loading = false;
        state.pujas = action.payload;
      })
      .addCase(fetchPujas.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch pujas';
      })
      .addCase(fetchPujaById.pending, (state) => {
        state.currentPujaLoading = true;
        state.error = null;
      })
      .addCase(fetchPujaById.fulfilled, (state, action) => {
        state.currentPujaLoading = false;
        state.currentPuja = action.payload;
        // Also update in pujas array if it exists
        const index = state.pujas.findIndex(p => p.id === action.payload.id);
        if (index !== -1) {
          state.pujas[index] = action.payload;
        } else {
          state.pujas.push(action.payload);
        }
      })
      .addCase(fetchPujaById.rejected, (state, action) => {
        state.currentPujaLoading = false;
        state.error = action.error.message || 'Failed to fetch puja';
      })
      .addCase(createPuja.fulfilled, (state, action) => {
        state.pujas.push(action.payload);
      })
      .addCase(updatePuja.fulfilled, (state, action) => {
        const index = state.pujas.findIndex(p => p.id === action.payload.id);
        if (index !== -1) {
          state.pujas[index] = action.payload;
        }
      })
      .addCase(deletePuja.fulfilled, (state, action) => {
        state.pujas = state.pujas.filter(p => p.id !== action.payload);
      })
      .addCase(updatePujaLine.fulfilled, (state, action) => {
        const { lineId, data } = action.payload;
        const updateLine = (puja: Puja) => {
          if (puja.lines) {
            const lineIndex = puja.lines.findIndex(line => line.id === lineId);
            if (lineIndex !== -1) {
              puja.lines[lineIndex] = { ...puja.lines[lineIndex], ...data };
            }
          }
        };
        state.pujas.forEach(updateLine);
        if (state.currentPuja) {
          updateLine(state.currentPuja);
        }
      })
      .addCase(deletePujaLine.fulfilled, (state, action) => {
        const lineId = action.payload;
        const removeLine = (puja: Puja) => {
          if (puja.lines) {
            puja.lines = puja.lines.filter(line => line.id !== lineId);
          }
        };
        state.pujas.forEach(removeLine);
        if (state.currentPuja) {
          removeLine(state.currentPuja);
        }
      })
      .addCase(addPujaLines.fulfilled, (state, action) => {
        const { pujaId } = action.payload;
        const responseData = action.payload.lines;
        const pujaIndex = state.pujas.findIndex(p => p.id === pujaId);
        if (pujaIndex !== -1) {
          state.pujas[pujaIndex].lines = responseData.lines;
        }
        if (state.currentPuja && state.currentPuja.id === pujaId) {
          state.currentPuja.lines = responseData.lines;
        }
      })
      .addCase(bulkUpdatePujaLineOrder.fulfilled, (state, action) => {
        const updates = action.payload;
        
        // Create a map of lineId to new order
        const orderMap = new Map<number, number>();
        updates.forEach(update => {
          orderMap.set(update.pujaLineId, update.changedOrder);
        });
        
        // Update all lines in all pujas with the new orders
        state.pujas.forEach(puja => {
          if (puja.lines) {
            puja.lines.forEach(line => {
              const newOrder = orderMap.get(line.id!);
              if (newOrder !== undefined) {
                line.order = newOrder;
              }
            });
            // Sort lines by order after updating
            puja.lines.sort((a, b) => (a.order || 0) - (b.order || 0));
          }
        });
        
        // Also update currentPuja if it exists
        if (state.currentPuja && state.currentPuja.lines) {
          state.currentPuja.lines.forEach(line => {
            const newOrder = orderMap.get(line.id!);
            if (newOrder !== undefined) {
              line.order = newOrder;
            }
          });
          state.currentPuja.lines.sort((a, b) => (a.order || 0) - (b.order || 0));
        }
      });
  },
});

export const { addPujaBidhiLink, addPujaSankalpaLink, addPujaGranthaLink, removePujaBidhiLink, removePujaSankalpaLink, removePujaGranthaLink, updatePujaBidhiLinkOrder, addPujaToState } = pujaSlice.actions;

export default pujaSlice.reducer;